import { createRef, type RefObject, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibCheckbox } from '@chewy/kib-choices-react'
import { KibInfoOutlineIcon } from '@chewy/kib-icons-react'
import { KibContainer } from '@chewy/kib-layout-react'
import { KibTooltip, KibTooltipTrigger } from '@chewy/kib-tooltip-react'
import { NumberUtils } from '@pbt/pbt-ui-components'

import type { Payment } from '~/api/graphql/generated/types'

import {
  autoSelectPayments,
  handleSelectPayment as handleSelectPaymentUtil,
  isPaymentDisabled,
  isPaymentSelected,
  type PaymentSelectionState,
} from '../utils/clientAccountCreditUtils'

import '@chewy/kib-tooltip-styles/dist/kib-tooltip.css'
import '@chewy/kib-choices-styles/dist/styles/kib-checkbox.css'
import styles from './ClientAccountCredit.module.scss'

interface ClientAccountCreditProps {
  businessId: string
  onAccountCreditChange?: (data: {
    accountCreditTotal: number
    selectedAccountCreditIds: string[]
  }) => void
  remainingBalance: number
  setRemainingBalance: (remainingBalance: number) => void
  setShouldRecalculate: (shouldRecalculate: boolean) => void
  shouldRecalculate?: boolean
  unappliedPayments: Payment[]
}

export const ClientAccountCredit = ({
  unappliedPayments,
  remainingBalance,
  setRemainingBalance,
  businessId,
  onAccountCreditChange,
  shouldRecalculate,
  setShouldRecalculate,
}: ClientAccountCreditProps) => {
  const { t } = useTranslation(['Common', 'Dialogs', 'Payments'])

  const [selectedPayments, setSelectedPayments] = useState<Payment[]>([])
  const [totalCreditAmount, setTotalCreditAmount] = useState(0)
  const [partiallyAppliedPayments, setPartiallyAppliedPayments] = useState<{
    [paymentId: string]: number
  }>({})
  const [isInitialized, setIsInitialized] = useState(false)

  const currentState: PaymentSelectionState = {
    selectedPayments,
    totalCreditAmount,
    remainingBalance,
    partiallyAppliedPayments,
  }

  const handleAutoSelectPayments = () => {
    const result = autoSelectPayments(unappliedPayments, remainingBalance)

    setSelectedPayments(result.selected)
    setTotalCreditAmount(result.currentTotalCredit)
    setRemainingBalance(result.workingRemainingBalance)
    setPartiallyAppliedPayments(result.partiallyApplied)
    if (onAccountCreditChange) {
      onAccountCreditChange({
        accountCreditTotal: result.currentTotalCredit,
        selectedAccountCreditIds: result.selected.map((payment) => payment.id),
      })
    }
  }

  useEffect(() => {
    if ((!isInitialized && unappliedPayments.length > 0) || shouldRecalculate) {
      handleAutoSelectPayments()
      setIsInitialized(true)
      if (shouldRecalculate) {
        setShouldRecalculate(false)
      }
    }
  }, [unappliedPayments, isInitialized, shouldRecalculate])

  const handleSelectPayment = (payment: Payment) => {
    const result = handleSelectPaymentUtil(payment, currentState)

    if (
      'newSelectedPayments' in result &&
      'balance' in result &&
      'total' in result
    ) {
      setSelectedPayments(result.newSelectedPayments)
      setRemainingBalance(result.balance)
      setTotalCreditAmount(result.total)
      setPartiallyAppliedPayments(result.newPartiallyApplied)
      if (onAccountCreditChange) {
        onAccountCreditChange({
          accountCreditTotal: result.total,
          selectedAccountCreditIds: result.newSelectedPayments.map(
            (paymentList) => paymentList.id,
          ),
        })
      }
    } else if (
      'newSelectedPayments' in result &&
      'newTotalCreditAmount' in result
    ) {
      setSelectedPayments(result.newSelectedPayments)
      setTotalCreditAmount(result.newTotalCreditAmount)
      setRemainingBalance(result.newRemainingBalance)
      setPartiallyAppliedPayments(result.newPartiallyAppliedPayments)
      if (onAccountCreditChange) {
        onAccountCreditChange({
          accountCreditTotal: result.newTotalCreditAmount,
          selectedAccountCreditIds: result.newSelectedPayments.map(
            (paymentList) => paymentList.id,
          ),
        })
      }
    }
  }
  const infoIconRefs = useMemo(() => {
    const refs: { [key: string]: RefObject<HTMLButtonElement> } = {}
    unappliedPayments.forEach((payment) => {
      refs[payment.id] = createRef<HTMLButtonElement>()
    })
    return refs
  }, [unappliedPayments])

  return (
    <div className={styles.clientAccountCreditGridItem}>
      <KibContainer className={styles.clientAccountCreditContainer}>
        <div className={styles.headerSection}>
          <h4 className={styles.sectionTitle}>
            {t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.APPLY_CLIENT_ACCOUNT_CREDIT_TITLE',
            )}
          </h4>
          <div className={styles.subtitle}>
            {t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.APPLY_CLIENT_ACCOUNT_CREDIT_SUBTITLE',
            )}
          </div>
        </div>
        <div className={styles.creditOptions}>
          {unappliedPayments.map((payment) => (
            <div className={styles.creditOption} key={payment.id}>
              <div className={styles.checkboxWithLabel}>
                <div className={styles.checkboxAndIcon}>
                  <KibCheckbox
                    checked={isPaymentSelected(payment, selectedPayments)}
                    className={styles.checkbox}
                    disabled={isPaymentDisabled(
                      payment,
                      selectedPayments,
                      remainingBalance,
                    )}
                    label={`${NumberUtils.formatMoney(payment.unappliedAmount)}`}
                    onChange={() => handleSelectPayment(payment)}
                  />

                  <div className={styles.infoIconContainer}>
                    <KibTooltipTrigger
                      className={styles.infoIconTrigger}
                      ref={infoIconRefs[payment.id]}
                    >
                      <KibInfoOutlineIcon className={styles.infoIcon} />
                    </KibTooltipTrigger>
                    <KibTooltip
                      placement="top"
                      popperModifiers={[]}
                      trigger={infoIconRefs[payment.id]}
                    >
                      <div className={styles.tooltipContent}>
                        <div className={styles.tooltipTitle}>
                          {payment.type?.name === 'Payment'
                            ? t(
                                'Dialogs:ADD_PAYMENT_DIALOG_V2.CREDIT_APPLIED_TOOLTIP_TITLE_PAYMENT',
                              )
                            : t('Payments:PAYMENT_TYPES.DEPOSIT')}
                        </div>
                        <div className={styles.tooltipInvoiceNumberContainer}>
                          <div className={styles.tooltipInvoiceList}>
                            {payment.links && payment.links.length > 0 ? (
                              <>
                                {payment.links
                                  .slice(0, 5)
                                  .map((link, index) => {
                                    const invoiceId = link.invoice?.id
                                    const invoiceNumber =
                                      link.invoiceNo ||
                                      t(
                                        'Dialogs:ADD_PAYMENT_DIALOG_V2.NO_INVOICE_NUMBER',
                                      )
                                    const linkKey = `${payment.id}-${invoiceId || invoiceNumber}-${index}`

                                    return (
                                      <div
                                        className={
                                          styles.tooltipInvoiceListItem
                                        }
                                        key={linkKey}
                                      >
                                        <span
                                          className={
                                            styles.tooltipInvoiceNumber
                                          }
                                        >
                                          <>{t('Common:INVOICE')}:&nbsp;</>
                                        </span>
                                        {invoiceId ? (
                                          <a
                                            className={
                                              styles.tooltipInvoiceNumberLink
                                            }
                                            href={`/invoice/${invoiceId}?businessId=${businessId}`}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                          >
                                            {invoiceNumber}
                                          </a>
                                        ) : (
                                          <span
                                            className={
                                              styles.tooltipInvoiceNumberLink
                                            }
                                          >
                                            {invoiceNumber}
                                          </span>
                                        )}
                                      </div>
                                    )
                                  })}
                                {payment.links.length > 5 && (
                                  <div
                                    className={styles.tooltipInvoiceListItem}
                                    key={`${payment.id}-billing-activity`}
                                  >
                                    <a
                                      className={
                                        styles.tooltipInvoiceNumberLink
                                      }
                                      href={`/balance/${payment.clientId}/?businessId=${businessId}`}
                                      rel="noopener noreferrer"
                                      target="_blank"
                                    >
                                      {t(
                                        'Dialogs:ADD_PAYMENT_DIALOG_V2.VIEW_ALL_INVOICES',
                                      )}
                                    </a>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className={styles.tooltipInvoiceListItem}>
                                <span className={styles.tooltipInvoiceNumber}>
                                  {`${t('Common:INVOICE')}:`}&nbsp;
                                </span>
                                <span
                                  className={styles.tooltipInvoiceNumberLink}
                                >
                                  {t(
                                    'Dialogs:ADD_PAYMENT_DIALOG_V2.NO_INVOICE_NUMBER',
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </KibTooltip>
                  </div>
                </div>
              </div>

              <div className={styles.remainingCreditRow}>
                {Boolean(partiallyAppliedPayments[payment.id]) && (
                  <div className={styles.remainingCreditAmount}>
                    {`${NumberUtils.formatMoney(partiallyAppliedPayments[payment.id])} ${t('Dialogs:ADD_PAYMENT_DIALOG_V2.CREDIT_APPLIED_REMAINING_SUBTITLE')}`}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className={styles.summarySection}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>
              {t('Dialogs:ADD_PAYMENT_DIALOG_V2.CREDIT_APPLIED')}
            </span>
            <span className={styles.summaryValue}>
              ({NumberUtils.formatMoney(totalCreditAmount)})
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>
              {t('Dialogs:ADD_PAYMENT_DIALOG_V2.REMAINING_BALANCE')}
            </span>
            <span className={styles.summaryValue}>
              {NumberUtils.formatMoney(remainingBalance)}
            </span>
          </div>
        </div>
      </KibContainer>
    </div>
  )
}
